import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { FlexLayoutModule } from '@angular/flex-layout'
import { ReactiveFormsModule } from '@angular/forms'
import { MatCardModule } from '@angular/material/card'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import {
  AppointmentModel,
  AppointmentSelectOption,
  Estimate,
  EstimateChargeDiscountType,
  EstimateData,
  EstimateFormGroup,
  EstimateWarning,
  getSelectedPhysicianIdFromAppointmentOptions,
  ModelClaimSearchResult,
} from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, defer, distinctUntilChanged, map, Observable, of, startWith } from 'rxjs'

import { ChargesDialogModule } from '../../../charges-dialog/charges-dialog.module'
import { ChargesTableFormModule } from '../../../charges-table-selector/charges-table-form.module'
import { EstimateTemplateMenuComponent } from '../../../estimate-template-menu'
import { FacilitySelectorModule } from '../../../facility-selector/facility-selector.module'
import { EstimateBalanceFormService } from '../../services/estimate-balance-form.service'
import { EstimateBalanceStateService } from '../../services/estimate-balance-state.service'
import { BalanceChargesAppointmentPickerComponent } from '../balance-charges-appointment-picker/balance-charges-appointment-picker.component'
import { BalanceChargesCocSummaryComponent } from '../balance-charges-coc-summary/balance-charges-coc-summary.component'
import {
  BalanceChargesResetType,
  BalanceChargesToolbarComponent,
} from '../balance-charges-toolbar/balance-charges-toolbar.component'

@Component({
  selector: 'mmx-balance-charges-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatMenuModule,
    ChargesDialogModule,
    ChargesTableFormModule,
    FacilitySelectorModule,
    BalanceChargesAppointmentPickerComponent,
    BalanceChargesCocSummaryComponent,
    BalanceChargesToolbarComponent,
    EstimateTemplateMenuComponent,
  ],
  templateUrl: './balance-charges-panel.component.html',
  styleUrls: ['./balance-charges-panel.component.scss'],
})
export class BalanceChargesPanelComponent implements OnChanges {
  @Input({ required: true }) estimate!: EstimateData | null
  @Input({ required: true }) estimateForm!: EstimateFormGroup
  @Input() appointmentOptions: AppointmentSelectOption[] = []
  @Input() loading = false
  @Input() claimSearchEnabled = false
  @Input() claimSearchPrefillQuery: string | null = null
  @Input() patientType: Estimate.PATIENT_TYPE = Estimate.PATIENT_TYPE.OutPatient
  @Input() cocMode = false
  @Input() unscheduledSelected = false
  @Input() warnings: EstimateWarning[] = []
  @Input() readOnly = false
  @Input() hideCalculate = false
  @Input() busy = false
  @Input() showMcsResponse = false

  // Mirror @Inputs as subjects so canCalculate$ recomputes on change without a per-CD getter walk.
  private readonly cocMode$ = new BehaviorSubject<boolean>(false)
  private readonly unscheduledSelected$ = new BehaviorSubject<boolean>(false)
  private readonly appointmentOptions$ = new BehaviorSubject<AppointmentSelectOption[]>([])

  @ViewChild(BalanceChargesToolbarComponent) toolbar?: BalanceChargesToolbarComponent

  @Output() readonly reset = new EventEmitter<BalanceChargesResetType>()
  @Output() readonly templateSelected = new EventEmitter<string>()
  @Output() readonly appointmentSelectionChange = new EventEmitter<string[]>()
  @Output() readonly addDiscountControl = new EventEmitter<{ index: number; type: EstimateChargeDiscountType }>()
  @Output() readonly removeDiscountControl = new EventEmitter<{ index: number }>()
  @Output() readonly chargesChanged = new EventEmitter<void>()
  @Output() readonly claimAdded = new EventEmitter<ModelClaimSearchResult | null>()
  @Output() readonly cocModeChange = new EventEmitter<boolean>()
  @Output() readonly unscheduledChange = new EventEmitter<boolean>()
  @Output() readonly calculate = new EventEmitter<void>()
  @Output() readonly mcsResponse = new EventEmitter<void>()

  constructor(
    private readonly state: EstimateBalanceStateService,
    private readonly formService: EstimateBalanceFormService,
  ) {}

  // Signal → observable bridge at the RxJS boundary. Field initializers so toObservable
  // runs inside the component's injection context.
  readonly selectedAppointments$: Observable<AppointmentModel[]> = toObservable(this.state.selectedAppointments)
  private readonly dirty$: Observable<boolean> = toObservable(this.state.dirty)

  // `defer` re-reads `estimateForm` lazily so a late form swap via ngOnChanges is picked up
  // without manual unwiring.
  readonly facilityIds$: Observable<string[]> = defer(() => {
    const ctrl = this.estimateForm?.controls.facilityId
    if (!ctrl) {
      return of<string[]>([])
    }
    return ctrl.valueChanges.pipe(
      startWith(ctrl.value),
      distinctUntilChanged(),
      map(id => (id ? [id] : [])),
    )
  })

  readonly selectedAppointmentIds$: Observable<string[]> = defer(() => {
    const ctrl = this.estimateForm?.controls.appointmentIds
    if (!ctrl) {
      return of<string[]>([])
    }
    return ctrl.valueChanges.pipe(
      startWith(ctrl.value ?? []),
      distinctUntilChanged((a, b) => a === b || (a.length === b.length && a.every((v, i) => v === b[i]))),
    )
  })

  readonly selectedPhysicianId$: Observable<string | null> = combineLatest([
    this.appointmentOptions$,
    this.selectedAppointmentIds$,
  ]).pipe(
    map(([appointmentOptions, selectedAppointmentIds]) =>
      getSelectedPhysicianIdFromAppointmentOptions(
        appointmentOptions,
        selectedAppointmentIds,
      ),
    ),
    distinctUntilChanged(),
  )

  // Stream replaces a per-CD getter — recomputes only on real input, feeds [canCalculate]
  // via async pipe.
  readonly canCalculate$: Observable<boolean> = defer(() => {
    const form = this.estimateForm
    if (!form) {
      return of(false)
    }
    return combineLatest([
      form.valueChanges.pipe(startWith(form.value)),
      // statusChanges fires when setErrors flips VALID↔INVALID without a value change —
      // the visit-number parser uses this status-only path.
      form.statusChanges.pipe(startWith(form.status)),
      this.cocMode$,
      this.unscheduledSelected$,
      // Saved-and-clean estimates don't expose Calculate — server already has the result.
      this.dirty$,
    ]).pipe(
      map(() => this.computeCanCalculate()),
      distinctUntilChanged(),
    )
  })

  get canResetByTemplate(): boolean {
    return !!this.estimate?.templateId
  }

  get canResetByEstimate(): boolean {
    return !!this.estimate?.id
  }

  // Backend rejects mode flips on a persisted estimate — lock the Unscheduled toggle
  // both ways so the user can't get stranded in a state the save will reject.
  get modeLocked(): boolean {
    return !!this.estimate?.id && this.estimate?.mode != null
  }

  get maxVisitNumber(): number | null {
    if (this.unscheduledSelected) {
      // Upper bound is unbounded in unscheduled mode (derived from charges);
      // the parser still enforces the positive-integer lower bound.
      return Number.MAX_SAFE_INTEGER
    }
    return this.cocMode ? this.state.selectedAppointmentIds().length : null
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('appointmentOptions' in changes) {
      this.appointmentOptions$.next(this.appointmentOptions)
    }
    if ('cocMode' in changes) {
      this.cocMode$.next(this.cocMode)
    }
    if ('unscheduledSelected' in changes) {
      this.unscheduledSelected$.next(this.unscheduledSelected)
    }
    if ('readOnly' in changes || 'estimateForm' in changes) {
      this.applyReadOnlyToFacility()
    }
  }

  // estimateForm is shared with the charges table; only toggle the facility control we own
  // here. Disabling the whole form would leak into the table's edit affordances.
  private applyReadOnlyToFacility(): void {
    const facility = this.estimateForm?.controls.facilityId
    if (!facility) {
      return
    }
    if (this.readOnly) {
      facility.disable({ emitEvent: false })
    } else {
      facility.enable({ emitEvent: false })
    }
  }

  private computeCanCalculate(): boolean {
    const chargesLen = this.estimateForm?.controls.charges?.length ?? 0
    if (chargesLen === 0) {
      return false
    }
    // Required by the API — `Facility.primaryKey.get({ id: '' })` 500s on the create/update.
    if (this.estimateForm?.controls.facilityId.invalid) {
      return false
    }
    // Persisted estimates need pending edits to justify Calculate; a fresh draft (no id) always
    // qualifies because the click is what creates it. Runs before the mode-specific branches.
    if (this.estimate?.id && !this.state.dirty()) {
      return false
    }
    if (this.unscheduledSelected) {
      // An auto-seeded row on one charge must not unlock Calculate while others are still
      // unconfigured (PIA-2569). Parser flags non-positive visit numbers as invalid.
      return this.formService.getCocChargesValid(this.estimateForm)
    }
    const appointmentsLen = this.estimateForm?.controls.appointmentIds?.value?.length ?? 0
    if (appointmentsLen === 0) {
      return false
    }
    if (this.cocMode && !this.formService.getCocChargesValid(this.estimateForm)) {
      return false
    }
    return true
  }

  onAppointmentChange(ids: string[]): void {
    this.appointmentSelectionChange.emit(ids)
  }

  onReset(type: BalanceChargesResetType): void {
    this.reset.emit(type)
  }

  onTemplateSelected(id: string): void {
    this.templateSelected.emit(id)
    this.toolbar?.closeTemplateMenu()
  }

  onAddDiscount(payload: { index: number; type: EstimateChargeDiscountType }): void {
    this.addDiscountControl.emit(payload)
  }

  onRemoveDiscount(payload: { index: number }): void {
    this.removeDiscountControl.emit(payload)
  }

  onChargesChanged(): void {
    this.chargesChanged.emit()
  }

  onClaimAdded(claim: ModelClaimSearchResult | null): void {
    this.claimAdded.emit(claim)
  }

  onCocModeChange(on: boolean): void {
    this.cocModeChange.emit(on)
  }

  onUnscheduledChange(on: boolean): void {
    this.unscheduledChange.emit(on)
  }

  onCalculate(): void {
    this.calculate.emit()
  }

  onMcsResponse(): void {
    this.mcsResponse.emit()
  }
}
